import * as React from 'react';

import { darken, transparentize } from 'polished';
import styled from 'styled-components';

import { COLOR, FONT_SIZE, RADIUS, SPACING } from '../../packages/constants';
import { HelperModal, QuestionMark } from '../helper-modal';

export interface TextInputProps {
    dataId: string;
    label: string;
    id: string;
    name: string;
    autofocus?: boolean;
    defaultValue?: string;
    disabled?: boolean;
    invalid?: boolean;
    hasHelper?: boolean;
    helperContent?: React.ReactNode;
    numeralsOnly?: boolean;
    placeholder?: string;
    validationMessage?: string;
    value?: string;
    onChange?: (e: any) => void;
    onBlur?: (e: any) => void;
    maxLength?: number;
    minLength?: number;
    thinLabel?: boolean;
    className?: string;
}

interface StyledTextLabelProps {
    disabled?: boolean;
    invalid?: boolean;
    thinLabel?: boolean;
}

interface StyledValidationMessageProps {
    invalid?: boolean;
}

const wrapperStyles = `
    display: flex;
    flex-direction: column;
`;

const Wrapper = styled.label`
    ${wrapperStyles}
`;

const WrapperHasHelper = styled.div`
    ${wrapperStyles}
`;

const StyledLabelWrapper = styled.div`
    display: inline-flex;
`;

const StyledTextLabel = styled.span<StyledTextLabelProps>`
    display: block;
    padding-bottom: ${SPACING.XX_SMALL};
    font-size: ${FONT_SIZE.SMALL};
    line-height: ${FONT_SIZE.MEDIUM};
    font-weight: ${(props: StyledTextLabelProps): string => (props.thinLabel ? '400' : '700')};
    color: ${(props: StyledTextLabelProps): string => (props.invalid ? COLOR.PRIMARY.RED : COLOR.MAIN.LABEL_TEXT)};
    opacity: ${(props: StyledTextLabelProps): string => (props.disabled ? '0.7' : '1')};
`;

const StyledLabelHasHelper = styled.label<StyledTextLabelProps>`
    display: inline-block;
    margin-right: ${SPACING.X_SMALL};
    padding-bottom: ${SPACING.XX_SMALL};
    font-size: ${FONT_SIZE.SMALL};
    line-height: ${FONT_SIZE.MEDIUM};
    font-weight: ${(props: StyledTextLabelProps): string => (props.thinLabel ? '400' : '700')};
    color: ${(props: StyledTextLabelProps): string => (props.invalid ? COLOR.PRIMARY.RED : COLOR.MAIN.LABEL_TEXT)};
    opacity: ${(props: StyledTextLabelProps): string => (props.disabled ? '0.7' : '1')};
`;

const StyledInput = styled.input<StyledTextLabelProps>`
    background-color: ${(props: StyledTextLabelProps): string =>
        props.disabled ? darken('0.1', COLOR.PRIMARY.WHITE) : COLOR.PRIMARY.WHITE};
    color: ${(props: StyledTextLabelProps): string =>
        props.disabled ? transparentize('0.5', COLOR.PRIMARY.BLACK) : COLOR.PRIMARY.BLACK};
    font-size: ${FONT_SIZE.SMALL};
    line-height: ${FONT_SIZE.MEDIUM};
    padding: ${SPACING.SMALL};
    border-width: 1px;
    border-style: solid;
    border-color: ${(props: StyledTextLabelProps): string =>
        props.invalid ? COLOR.PRIMARY.RED : COLOR.MAIN.FORM_FIELD};
    border-radius: ${RADIUS.DEFAULT};

    &::placeholder {
        opacity: 0.65;
    }
`;

const StyledValidationMessage = styled.div<StyledValidationMessageProps>`
    font-size: ${FONT_SIZE.X_SMALL};
    line-height: ${FONT_SIZE.SMALL};
    color: ${(props: StyledValidationMessageProps): string =>
        props.invalid ? COLOR.PRIMARY.RED : COLOR.PRIMARY.BLACK};
    padding-top: ${SPACING.SMALL};
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
    (
        {
            dataId,
            label,
            id,
            hasHelper,
            helperContent,
            placeholder,
            name,
            autofocus,
            defaultValue,
            disabled,
            invalid,
            validationMessage,
            onChange,
            onBlur,
            maxLength,
            minLength,
            thinLabel,
            value,
            className,
            ...props
        },
        ref,
    ) => {
        return hasHelper ? (
            <WrapperHasHelper className={className} {...props}>
                <StyledLabelWrapper>
                    <StyledLabelHasHelper
                        data-test-id={`${dataId}-label`}
                        thinLabel={thinLabel}
                        invalid={invalid}
                        disabled={disabled}
                        htmlFor={id}
                    >
                        {label}
                    </StyledLabelHasHelper>
                    <HelperModal dataId={dataId} modalContent={helperContent}>
                        <QuestionMark />
                    </HelperModal>
                </StyledLabelWrapper>
                <StyledInput
                    type="text"
                    data-test-id={`${dataId}-input`}
                    id={id}
                    autoFocus={autofocus}
                    name={name}
                    placeholder={placeholder}
                    maxLength={maxLength}
                    minLength={minLength}
                    invalid={invalid}
                    disabled={disabled}
                    {...(typeof value !== 'undefined' && { value })}
                    {...(typeof defaultValue !== 'undefined' && { defaultValue })}
                    ref={ref}
                />
                {validationMessage && (
                    <StyledValidationMessage invalid={invalid}>{validationMessage}</StyledValidationMessage>
                )}
            </WrapperHasHelper>
        ) : (
            <Wrapper data-test-id={`${dataId}-label`} className={className} {...props}>
                <StyledTextLabel invalid={invalid} disabled={disabled} thinLabel={thinLabel}>
                    {label}
                </StyledTextLabel>
                <StyledInput
                    type="text"
                    data-test-id={`${dataId}-input`}
                    id={id}
                    autoFocus={autofocus}
                    name={name}
                    placeholder={placeholder}
                    maxLength={maxLength}
                    minLength={minLength}
                    invalid={invalid}
                    disabled={disabled}
                    {...(typeof value !== 'undefined' && { value })}
                    {...(typeof defaultValue !== 'undefined' && { defaultValue })}
                    onChange={onChange}
                    onBlur={onBlur}
                    ref={ref}
                />
                {validationMessage && (
                    <StyledValidationMessage invalid={invalid}>{validationMessage}</StyledValidationMessage>
                )}
            </Wrapper>
        );
    },
);
TextInput.displayName = 'TextInput';
